import { SortableHeader } from '@/components/search/SortableHeader'
import React, { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Services } from '@/services/services'
import { CurrentSession } from '@/services/repository/session'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { BackButton } from '@/components/layout/NavBar'
import { SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable'
import { ErrorBoxComponent } from '@/components/forms/common/FormBase'
import { useModal } from '@/components/modal/Modal'
import { Menu, MenuPopupBody } from '@/popups/menupopup/menu'
import { Namespace, namespaceFormErrors } from './common'

type Props = { services: Services, session: CurrentSession }
type PopupProps = Props & { onClose: () => void }

function NamespaceForm({ services, session, onClose, namespace }: PopupProps & { namespace?: Namespace }) {
  const form = useForm<{ name: string, _general?: string }>({ defaultValues: { name: namespace?.name ?? '' } })
  const submit = async (data: { name: string }) => {
    form.clearErrors()
    try {
      const r = await services.session.fetchWithToken(namespace ? `/api/communal/namespaces/byid/${namespace.id}/update` : '/api/communal/namespaces', {
        method: namespace ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: data.name.trim() })
      })
      if (!r.ok) { await namespaceFormErrors(form, session, r); return }
      onClose()
    } catch { form.setError('_general', { message: session.translates.for('errors.namespace_request_failed') }) }
  }
  return <form className='main-form' onSubmit={form.handleSubmit(submit)} autoComplete='off'>
    <div className='form-field'>
      <label htmlFor='namespace-name'>{session.translates.for('namespace.name')}</label>
      <input type='text' id='namespace-name' {...form.register('name', { required: session.translates.for('errors.required'), maxLength: { value: 250, message: session.translates.for('errors.namespace_name_invalid') }, validate: value => !!value.trim() || session.translates.for('errors.required') })} />
      {ErrorBoxComponent(form, session.translates, 'name')}
    </div>
    {ErrorBoxComponent(form, session.translates, '_general')}
    <button type='submit' className='submit-button' disabled={form.formState.isSubmitting}>{session.translates.for('namespace.save')}</button>
  </form>
}

function RemoveNamespace({ services, session, onClose, namespace }: PopupProps & { namespace: Namespace }) {
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)
  const remove = async () => {
    if (busy) return
    setBusy(true); setError(undefined)
    try {
      const r = await services.session.fetchWithToken(`/api/communal/namespaces/byid/${namespace.id}/delete`, { method: 'DELETE' })
      if (r.ok) { onClose(); return }
      const data = await r.json()
      setError(session.translates.for(data.errors?._general?.message ?? 'errors.namespace_request_failed'))
    } catch { setError(session.translates.for('errors.namespace_request_failed')) }
    finally { setBusy(false) }
  }
  return <div className='dialog'>
    <div className='dialog-text'>{session.translates.for('namespace.delete_ask').replace('{name}', namespace.name)}</div>
    <div className='dialog-btns'>
      <div className='dialog-btn' role='button' tabIndex={0} onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          event.currentTarget.click()
        }
      }} onClick={onClose}>
        <i className='svgfont-no' aria-hidden='true' />
        {session.translates.for('elements.dialog.yesno.no')}
      </div>
      <div className='dialog-btn' role='button' tabIndex={0} aria-disabled={busy} onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          event.currentTarget.click()
        }
      }} onClick={remove}>
        <i className='svgfont-yes' aria-hidden='true' />
        {session.translates.for('elements.dialog.yesno.yes')}
      </div>
      {error && <div className='dialog-error'>{error}</div>}
    </div>
  </div>
}

type Manager = { id: string, username: string, email: string, role: string, isRelated?: boolean }
function NamespaceManagers({ services, session, namespace }: PopupProps & { namespace: Namespace }) {
  const [error, setError] = useState<string>()
  const params: SearchTableParams<Manager> = {
    fetch: q => services.session.fetchWithToken(`/api/communal/namespaces/byid/${namespace.id}/managers/search?${new URLSearchParams({ pageNumber: String(q.pageNumber), pageSize: String(q.pageSize), search: q.search, sortBy: q.sortBy || '', sortDirection: q.sortDirection || '' })}`).then(r => {
      if (!r.ok) throw new Error(session.translates.for('errors.namespace_request_failed'))
      return r.json()
    }),
    pageQuery: { pageNumber: 1, pageSize: 30, search: '' },
    defaultSortBy: 'username',
    header: (query, onSort) => <><div className='hcol' /><SortableHeader field='username' query={query} onSort={onSort}>{session.translates.for('elements.table.users.username')}</SortableHeader><SortableHeader field='email' query={query} onSort={onSort}>{session.translates.for('elements.table.users.email')}</SortableHeader><SortableHeader field='role' query={query} onSort={onSort}>{session.translates.for('namespace.role')}</SortableHeader></>,
    row: (row, state, setState) => <>
      <div className='col icocol' role='checkbox' tabIndex={0} aria-label={session.translates.for('namespace.assign_manager') + ': ' + row.username} aria-checked={!!row.isRelated} aria-disabled={row.isRelated === undefined} onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          event.currentTarget.click()
        }
      }} onClick={async () => {
          if (row.isRelated === undefined) return
          const original = row.isRelated
          const replace = (value: boolean | undefined) => setState(previous => previous ? { ...previous, data: previous.data?.map(item => item.id === row.id ? { ...item, isRelated: value } : item) } : previous)
          setError(undefined); replace(undefined)
          try {
            const r = await services.session.fetchWithToken(`/api/communal/namespaces/byid/${namespace.id}/relations/managers/${row.id}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ related: !original }) })
            if (!r.ok) throw new Error('Relation failed')
            replace(!original)
          } catch { replace(original); setError(session.translates.for('errors.namespace_request_failed')) }
      }}>
        {row.isRelated === undefined
          ? <div className='spinner' />
          : <i className={row.isRelated ? 'svgfont-checkbox' : 'svgfont-checkbox-blank'} />}
      </div>
      <div className='col'>{row.username}</div><div className='col'>{row.email}</div><div className='col'>{session.translates.for(`users.role.${row.role}`, row.role)}</div>
    </>,
    translates: session.translates
  }
  return <><h3>{namespace.name}</h3>{error && <p className='errorMessage'>{error}</p>}<SearchTable {...params} /></>
}

function NamespaceMenuPopup({ services, session, onClose, namespace, onChanged }: PopupProps & { namespace: Namespace, onChanged: () => void }) {
  const { openModal } = useModal()
  const menu: Menu = {
    title: namespace.name,
    rows: [
      {
        icon: 'svgfont-edit',
        title: 'namespace.edit',
        action: () => openModal(props => <NamespaceForm {...props} services={services} session={session} namespace={namespace} onClose={() => { props.onClose(); onChanged() }} />)
      },
      {
        icon: 'svgfont-users',
        title: 'namespace.managers',
        action: () => openModal(props => <NamespaceManagers {...props} services={services} session={session} namespace={namespace} />)
      },
      {
        icon: 'svgfont-remove',
        title: 'namespace.delete',
        action: () => openModal(props => <RemoveNamespace {...props} services={services} session={session} namespace={namespace} onClose={() => { props.onClose(); onChanged() }} />)
      }
    ]
  }
  return <MenuPopupBody session={session} menu={menu} onClose={onClose} />
}

export function NamespacesPage({ services, session }: Props) {
  const ref = useRef<SearchTableRef>(null)
  const { openModal } = useModal()
  const params: SearchTableParams<Namespace> = {
    fetch: q => services.session.fetchWithToken(`/api/communal/namespaces/search?${new URLSearchParams({ pageNumber: String(q.pageNumber), pageSize: String(q.pageSize), search: q.search, sortBy: q.sortBy || '', sortDirection: q.sortDirection || '' })}`).then(r => {
      if (!r.ok) throw new Error(session.translates.for('errors.namespace_request_failed'))
      return r.json()
    }),
    pageQuery: { pageNumber: 1, pageSize: 30, search: '' },
    toolbox: () => <div className='tool' role='button' tabIndex={0} aria-label={session.translates.for('namespace.add')} onKeyDown={event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        event.currentTarget.click()
      }
    }} onClick={() => openModal(props => <NamespaceForm {...props} services={services} session={session} onClose={() => { props.onClose(); ref.current?.refresh() }} />)}><i className='svgfont-add' /></div>,
    defaultSortBy: 'name',
    header: (query, onSort) => <><SortableHeader field='name' query={query} onSort={onSort}>{session.translates.for('namespace.name')}</SortableHeader><div className='hcol' /></>,
    row: row => <>
      <div className='col'>{row.name}</div>
      <div className='col icocol' role='button' tabIndex={0} aria-label={row.name} onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          event.currentTarget.click()
        }
      }} onClick={() => openModal(props => <NamespaceMenuPopup {...props} services={services} session={session} namespace={row} onChanged={() => ref.current?.refresh()} />)}>
        <i className='svgfont-menu' />
      </div>
    </>,
    translates: session.translates
  }
  return <LoggedInPageLayout pageName='namespaces' services={services} session={session} navbarLeft={<BackButton services={services} translates={session.translates} />}>
    <SearchTable ref={ref} {...params} />
  </LoggedInPageLayout>
}
